
import Vue from 'vue';
import _get from 'lodash/get';
import _find from 'lodash/find';
import _findLast from 'lodash/findLast';
import _isEmpty from 'lodash/isEmpty';
import { mapActions, mapGetters, mapState } from 'vuex';
import {
	AppKey,
	AppOffer,
	DeploymentImage,
	HardwareOffer,
	ReleaseStatus,
	Session,
	SessionOptions,
	SetupSessionPrefill,
	SoftwareOffer,
	Workspace,
	getHardwareHourlyPrice,
	sortDeploymentImages,
	APP_OFFER_TYPE,
	APP_KEY,
	CLUB_KEY,
	RELEASE_STATUS
} from '@run-diffusion/shared';
import BalanceCard from '@/components/BalanceCard/BalanceCard.vue';
import TimeYourSessionInputs from '@/components/TimeYourSessionInputs.vue';
import SessionSoundsSettings from '@/components/SessionSoundsSettings.vue';
import HowToStopEarlyNotice from '@/components/HowToStopEarlyNotice.vue';
import EnableApiSettings from '@/components/EnableApiSettings.vue';
import StorageCapCard from '@/components/StorageCapCard/StorageCapCard.vue';
import ActionsIsland from '@/components/ActionsIsland.vue';
import { SNACKBAR_STATUS, STOPPING_SESSION_STATES } from '@/constants/constants';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import {
	getPersistExtensions,
	getProxyAdminToken,
	getWorkspaceId,
	removeWorkspaceId,
	removePersistExtensions,
	setPersistExtensions,
	setWorkspaceId,
} from '@/utils/localStorage';
import { db, functions } from '@/firebase';
import type { FirebaseUser } from '@/firebase';
import BigNumber from 'bignumber.js';
import { calcRdBalanceToRunnitTokens, formatCooldownTimer, getCooldownTimeBucketShort, getCooldownChipLabel, hundredthsToTokens, TOKEN_COOLDOWN_TIME_BUCKET } from '@run-diffusion/shared';
import moment from 'moment-timezone';
import { MyBalanceMixin, TeamUserMixin } from '@/mixins';
import FullSelectionPreviewCard from '@/views/Sessions/SetupSession/FullSelectionPreviewCard.vue';
import HardwareOffersList from '@/views/Sessions/HardwareOffersList/HardwareOffersList.vue';
import SoftwareOffersList from '@/views/Sessions/SoftwareOffersList/SoftwareOffersList.vue';
import DeploymentImagesList from '@/views/Sessions/DeploymentImagesList/DeploymentImagesList.vue';
import SetupSessionTerms from '@/views/Sessions/SetupSession/SetupSessionTerms.vue';
import BackButton from '@/components/base/BackButton.vue';
import StorageOptionsList from '@/views/Sessions/StorageOptionsList/StorageOptionsList.vue';
import LoadingSVG from '@/assets/LoadingSVG.vue';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import WindowStepper from '@/components/WindowStepper.vue';
import InfoMessageSmall from '@/components/base/InfoMessageSmall.vue';
import { APP_OFFER_IS_BETA_MESSAGE } from '@/views/Sessions/AppOffersList/utils';
import EnablePersistExtensionsSettings from '@/components/EnablePersistExtensionsSettings.vue';
import PreloadModelPreview from '@/components/PreloadModelPreview.vue';
import UnlockRdTrial from '@/components/UnlockRdTrial.vue';
import { ROUTER } from '@/router/constants';
import CooldownProgressMenu from '@/views/Runnits/CooldownProgressMenu.vue';
import CooldownLabelButton from '@/components/CooldownLabelButton.vue';
import { getDomainClaimRestrictionMessage } from '@/utils/domainClaimRestriction';

export default Vue.extend({
	name: 'SetupSession',
	metaInfo: {
		title: 'Setup Session | RunDiffusion',
	},
	mixins: [
		TeamUserMixin,
		MyBalanceMixin,
	],
	data () {
		let windowItemIter: number = 0;
		return {
			APP_OFFER_IS_BETA_MESSAGE,
			APP_KEY,
			CLUB_KEY,
			RUNNITS_OWNER_SELECTION,
			LAUNCH_TEXT: 'LAUNCH',
			PRELOAD_MODEL_PREVIEW_MESSAGE_HTML: 'This model will download in the background while your session boots!',
			dialogOpen: false,
			purchasing: false,
			selectedScheduledStopInHrs: 0,
			selectedScheduledStopInMins: 0,
			apiEnabled: false,
			persistExtensions: false,
			selectedWorkspaceId: null,

			appOffer: null,
			hardwareOffer: null,
			softwareOffer: null,
			deploymentImage: null,
			storageOption: null,

			confirmCancelDialogOpen: false,
			forceWizardFlow: false,
			shouldAttemptHydrateFromSessionPrefill: true,

			startingWindowViewForNewUserTrialFlow: 0,
			windowView: 0,
			largestWindowView: 0,
			PICK_HARDWARE_WINDOW_ITEM: windowItemIter++,
			PICK_SOFTWARE_WINDOW_ITEM: windowItemIter++,
			PICK_STORAGE_OPTION_WINDOW_ITEM: windowItemIter++,
			SESSION_SETTINGS_WINDOW_ITEM: windowItemIter++,
			PREVIEW_WINDOW_ITEM: windowItemIter++,
			LAUNCH_WINDOW_ITEM: windowItemIter++,
			WINDOW_VIEW_LENGTH: windowItemIter,
		};
	},
	created () {
		this.persistExtensions = getPersistExtensions();
		this.startingWindowViewForNewUserTrialFlow = this.SESSION_SETTINGS_WINDOW_ITEM;
	},
	mounted () {
		let appOffer = null;
		if (this.$route.query.app) {
			appOffer = this.appOffersMap[this.$route.query.app];
		}
		if (appOffer) {
			this.appOffer = appOffer;
		} else {
			this.goBackToSessionsPage(false);
		}
		if (this.isTeamSessionForCooldown && this.hasTokenCooldownPermission && this.team?.id) {
			this.fetchTokenCooldownStatus();
		}
	},
	watch: {
		appOffer: {
			immediate: true,
			handler (newVal: AppOffer, oldVal: AppOffer) {
				if (newVal && newVal !== oldVal) {
					this.bindSetupSessionPrefill({
						authUser: this.authUser,
						appKey: newVal.app,
					});
				}
				if (newVal !== oldVal) {
					this.hydrateFromSetupSessionPrefill(newVal, this.setupSessionPrefill, this.hardwareOffers, this.softwareOffers);
					this.decideToForceWizardFlow(newVal, this.boundPropsSetupSessionPrefill, this.loadingSetupSessionPrefill, this.setupSessionPrefill);
				}
				if (newVal && newVal.id !== (oldVal && oldVal.id)) {
					if (this.isNewUserTrialFlow) {
						// Reduce number of clicks for new users
						this.windowView = this.startingWindowViewForNewUserTrialFlow;
					} else {
						this.windowView = 0;
					}
					this.largestWindowView = this.windowView;
				}
			},
		},
		setupSessionPrefill: {
			immediate: true,
			async handler (newVal: SetupSessionPrefill, oldVal: SetupSessionPrefill) {
				if (newVal !== oldVal) {
					this.hydrateFromSetupSessionPrefill(this.appOffer, newVal, this.hardwareOffers, this.softwareOffers);
					this.decideToForceWizardFlow(this.appOffer, this.boundPropsSetupSessionPrefill, this.loadingSetupSessionPrefill, newVal);
					await this.rolloverSetupSessionPrefillReleaseStatus(newVal, this.softwareOffers);
				}
			},
		},
		boundPropsSetupSessionPrefill: {
			immediate: true,
			handler (newVal: { authUser: FirebaseUser, appKey: AppKey }) {
				this.decideToForceWizardFlow(this.appOffer, newVal, this.loadingSetupSessionPrefill, this.setupSessionPrefill);
			},
		},
		loadingSetupSessionPrefill: {
			immediate: true,
			handler (newVal: boolean) {
				this.decideToForceWizardFlow(this.appOffer, this.boundPropsSetupSessionPrefill, newVal, this.setupSessionPrefill);
			},
		},
		hardwareOffers: {
			immediate: true,
			handler (newVal: HardwareOffer[], oldVal: HardwareOffer[]) {
				if (newVal !== oldVal) {
					this.hydrateFromSetupSessionPrefill(this.appOffer, this.setupSessionPrefill, newVal, this.softwareOffers);
				}
			},
		},
		softwareOffers: {
			immediate: true,
			async handler (newVal: SoftwareOffer[], oldVal: SoftwareOffer[]) {
				if (newVal !== oldVal) {
					this.hydrateFromSetupSessionPrefill(this.appOffer, this.setupSessionPrefill, this.hardwareOffers, newVal);
					await this.rolloverSetupSessionPrefillReleaseStatus(this.setupSessionPrefill, newVal);
				}
			},
		},
		deploymentImages: {
			immediate: true,
			handler (newVal: DeploymentImage[], oldVal: DeploymentImage[]) {
				if (newVal !== oldVal && this.mobiusEnabled) {
					this.hydrateFromSetupSessionPrefill(this.appOffer, this.setupSessionPrefill, this.hardwareOffers, this.softwareOffers);
				}
			},
		},
		mobiusEnabled: {
			immediate: false,
			handler () {
				// Toggling between Default and Mobius means the prior hardware/software/image picks are stale.
				this.shouldAttemptHydrateFromSessionPrefill = true;
				this.hydrateFromSetupSessionPrefill(this.appOffer, this.setupSessionPrefill, this.hardwareOffers, this.softwareOffers);
			},
		},
		workspaces: {
			immediate: true,
			handler (newVal: Workspace[]) {
				const lastSelectedWorkspaceId: string = this.selectedWorkspaceId;
				const localStorageWorkspaceId: string = getWorkspaceId();
				let selectedWorkspace: Workspace = null;

				if (newVal && newVal.length) {
					if (!selectedWorkspace && lastSelectedWorkspaceId) {
						selectedWorkspace = _find(newVal, ['id', lastSelectedWorkspaceId]) || null;
					}
					if (!selectedWorkspace && localStorageWorkspaceId) {
						selectedWorkspace = _find(newVal, ['id', localStorageWorkspaceId]) || null;
					}
					if (!selectedWorkspace) {
						selectedWorkspace = newVal[0];
					}

					this.selectedWorkspaceId = selectedWorkspace ? selectedWorkspace.id : null;
				}
			},
		},
		forceWizardFlow: {
			immediate: false,
			handler (newVal: boolean, oldVal: boolean) {
				if (newVal !== oldVal) {
					this.dialogOpen = newVal;
				}
			},
		},
		windowView: {
			immediate: true,
			handler (newVal: number, oldVal: number) {
				this.largestWindowView = Math.max(this.largestWindowView, newVal);
				if (newVal !== oldVal) {
					this.$scrollTo('#setup-session-dialog-scroll-anchor', 500, {
						container: '#setup-session-dialog-scroll-container',
					});
				}
			},
		},
		team: {
			handler (newVal: any) {
				if (this.isTeamSessionForCooldown && this.hasTokenCooldownPermission && newVal?.id) {
					this.fetchTokenCooldownStatus();
				}
			},
		},
		shouldRefetchTokenCooldown: {
			handler (shouldRefetch: boolean) {
				if (shouldRefetch && this.isTeamSessionForCooldown && this.hasTokenCooldownPermission && this.team?.id) {
					this.fetchTokenCooldownStatus();
				}
			},
		},
	},
	computed: {
		...mapState([
			'authUser',
			'user',
			'team',
			'teamBalanceAccount',
			'workspaces',
			'preloadModel',
			'currSessions',
			'appOffersMap',
			'boundPropsSetupSessionPrefill',
			'loadingSetupSessionPrefill',
			'setupSessionPrefill',
			'hardwareOffers',
			'softwareOffers',
			'deploymentImages',
			'runnitState',
			'realTimeMoment',
		]),
		...mapGetters([
			'isNewUserTrialFlow',
			'userClubOfferBenefits',
			'needsUnlockRdTrial',
			'activeDomainClaimRestriction',
			'displayLabels',
			'companyClubOfferBenefits',
			'mobiusEnabled',
		]),
		sessionLaunchBlockedByDomainClaim (): boolean {
			return !!this.activeDomainClaimRestriction;
		},
		canUseMobius (): boolean {
			return !!this.user;
		},
		domainClaimLaunchMessage (): string {
			return getDomainClaimRestrictionMessage(this.activeDomainClaimRestriction, this.displayLabels.team) || '';
		},
		sessionDomainClaimLockBannerText (): string {
			if (!this.domainClaimLaunchMessage) return '';
			return `${this.domainClaimLaunchMessage} Open-source session launch is not available in this account context—switch to your organization's ${this.displayLabels.team} in the toolbar or change your sign-in email.`;
		},
		domainClaimLaunchTooltip (): string {
			return this.sessionDomainClaimLockBannerText;
		},
		// A deactivated team can't launch new sessions: launchDisabled holds the button, and the
		// purchase is refused if one gets through anyway
		isTeamDeactivatedForLaunch (): boolean {
			return !!(
				this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
				this.team &&
				!this.isTeamActive
			);
		},
		teamDeactivatedLaunchMessage (): string {
			return `This ${this.displayLabels.team} is deactivated, so it can't launch new sessions. Switch to another account, or ask an admin to reactivate it.`;
		},
		canGoNegative () {
			return !!(
				this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
				this.teamBalanceAccount?.type === 'TEAM' &&
				this.companyClubOfferBenefits.isEnterprise &&
				!this.team.allotmentSettings?.isBoundToBudget
			);
		},
		isWizardFlow () {
			return this.$vuetify.breakpoint.smAndDown || this.forceWizardFlow;
		},
		dialogTitle () {
			if (this.windowView === this.PICK_HARDWARE_WINDOW_ITEM) return 'Choose Hardware to Run';
			if (this.windowView === this.PICK_SOFTWARE_WINDOW_ITEM) return this.mobiusEnabled ? 'Choose Build' : 'Choose Software to Run';
			if (this.windowView === this.PICK_STORAGE_OPTION_WINDOW_ITEM) return 'Choose Your Storage Option';
			if (this.windowView === this.SESSION_SETTINGS_WINDOW_ITEM) return 'Session Settings';
			if (this.windowView === this.PREVIEW_WINDOW_ITEM) return 'Preview Session';
			if (this.windowView === this.LAUNCH_WINDOW_ITEM) return 'Launch Session';
			return 'Setup Session';
		},
		shouldShowPersistExtensionsSetting () {
			return !!(
				this.appOffer &&
				this.appOffer.showPersistExtensions &&
				(
					(this.user && this.userClubOfferBenefits.isCc) ||
					this.isTeamActive
				)
			);
		},
		shouldSetCivitaiPreloadModel () {
			return !!(
				this.appOffer &&
				[
					APP_OFFER_TYPE.IMAGE_GEN,
					APP_OFFER_TYPE.IMAGE_TRAIN,
					APP_OFFER_TYPE.FILE,
				].includes(this.appOffer.type) &&
				this.preloadModel.civitai &&
				this.preloadModel.civitai.model &&
				this.preloadModel.civitai.modelVersion
			);
		},
		hardwareHourlyPrice () {
			if (!this.hardwareOffer || !this.user) return 0;
			return getHardwareHourlyPrice(this.hardwareOffer, this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM && this.team && this.team.isActive ? this.team.id : null, this.user.club);
		},
		cannotLaunchDueToSingleOnlyApp () {
			return !!(
				this.appOffer &&
				this.appOffer.singleOnly &&
				this.currSessions.some((session: Session) => (
					session.apps.includes(this.appOffer.app) &&
					!STOPPING_SESSION_STATES.includes(session.state)
				))
			);
		},
		continueDisabled () {
			return !!(
				!this.appOffer ||
				(this.windowView === this.PICK_HARDWARE_WINDOW_ITEM && !this.hardwareOffer) ||
				(this.windowView === this.PICK_SOFTWARE_WINDOW_ITEM && (this.mobiusEnabled ? !this.deploymentImage : !this.softwareOffer)) ||
				(
					this.windowView === this.SESSION_SETTINGS_WINDOW_ITEM &&
					!this.selectedScheduledStopInHrs &&
					!this.selectedScheduledStopInMins
				) ||
				(
					this.windowView === this.PICK_STORAGE_OPTION_WINDOW_ITEM &&
					this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
					!this.selectedWorkspaceId
				) ||
				this.purchasing
			);
		},
		launchDisabled () {
			return !!(
				!this.appOffer ||
				!this.hardwareOffer ||
				(this.mobiusEnabled ? !this.deploymentImage : !this.softwareOffer) ||
				(
					!this.selectedScheduledStopInHrs &&
					!this.selectedScheduledStopInMins
				) ||
				(
					this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
					!this.isTeamActive
				) ||
				(
					this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
					!this.selectedWorkspaceId
				) ||
				this.cannotLaunchDueToSingleOnlyApp ||
				this.purchasing ||
				this.tokenCooldownBlocksLaunch ||
				this.sessionLaunchBlockedByDomainClaim
			);
		},
		purchaseMins () {
			if (
				this.selectedScheduledStopInHrs ||
				this.selectedScheduledStopInMins
			) {
				return (this.selectedScheduledStopInHrs * 60) + this.selectedScheduledStopInMins;
			}
			return 0;
		},
		isTeamSessionForCooldown () {
			return !!(
				this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
				this.team?.id &&
				this.team?.companyId
			);
		},
		estimatedSessionTokenCostInHundredths () {
			if (!this.purchaseMins || !this.hardwareHourlyPrice) return 0;
			const costCents = new BigNumber(this.purchaseMins).div(60).times(this.hardwareHourlyPrice).integerValue(BigNumber.ROUND_HALF_UP).toNumber();
			return calcRdBalanceToRunnitTokens(costCents, true);
		},
		tokenCooldownBlocksLaunch () {
			if (!this.isTeamSessionForCooldown || !this.hasTokenCooldownPermission) return false;
			const cooldownData = this.runnitState.tokenCooldownData?.cooldownData;
			if (!cooldownData || typeof cooldownData.tokensRemaining !== 'number') return false;
			return cooldownData.tokensRemaining < this.estimatedSessionTokenCostInHundredths;
		},
		cooldownEndsAt () {
			return this.runnitState.tokenCooldownData?.cooldownEndsAt || null;
		},
		shouldRefetchTokenCooldown () {
			const endsAt = this.cooldownEndsAt;
			if (!endsAt || !this.realTimeMoment) return false;
			return moment(endsAt).isSameOrBefore(this.realTimeMoment);
		},
		sessionCooldownData () {
			return this.runnitState.tokenCooldownData?.cooldownData || null;
		},
		sessionIsCooldownActive () {
			const endsAt = this.cooldownEndsAt;
			if (!endsAt || !this.realTimeMoment) return false;
			return moment(endsAt).isAfter(this.realTimeMoment);
		},
		sessionCooldownCountdownData () {
			if (!this.sessionIsCooldownActive || !this.cooldownEndsAt || !this.realTimeMoment) return null;
			const resetAtMoment = moment(this.cooldownEndsAt);
			const seconds = Math.max(0, resetAtMoment.diff(this.realTimeMoment, 'seconds'));
			const hours = Math.floor(seconds / 3600);
			const minutes = Math.floor((seconds % 3600) / 60);
			const secs = seconds % 60;
			return { hours, minutes, seconds: secs, totalSeconds: seconds };
		},
		sessionCooldownCountdownText () {
			if (!this.sessionIsCooldownActive || !this.cooldownEndsAt || !this.realTimeMoment) return '';
			const resetAtMoment = moment(this.cooldownEndsAt);
			if (resetAtMoment.diff(this.realTimeMoment, 'seconds') <= 0) return '';
			return resetAtMoment.from(this.realTimeMoment, true);
		},
		/** Timer for launch button (shared util). */
		sessionCooldownChipTimerText () {
			if (!this.cooldownEndsAt || !this.realTimeMoment) return '';
			const resetAtMoment = moment(this.cooldownEndsAt);
			const totalSeconds = Math.max(0, resetAtMoment.diff(this.realTimeMoment, 'seconds'));
			const bucketShort = getCooldownTimeBucketShort(this.sessionCooldownData?.timeBucket);
			return formatCooldownTimer(totalSeconds, bucketShort);
		},
		/** Chip label when launch blocked by cooldown (shared util). */
		sessionCooldownChipLabel () {
			if (!this.tokenCooldownBlocksLaunch || !this.sessionCooldownData) return '';
			return getCooldownChipLabel(
				this.estimatedSessionTokenCostInHundredths,
				this.sessionCooldownData.threshold,
				this.sessionCooldownChipTimerText
			);
		},
		sessionCooldownTokensRemainingCount () {
			if (!this.sessionCooldownData || typeof this.sessionCooldownData.tokensRemaining !== 'number') return 0;
			return hundredthsToTokens(this.sessionCooldownData.tokensRemaining);
		},
		sessionCooldownThresholdTokens () {
			if (!this.sessionCooldownData || typeof this.sessionCooldownData.threshold !== 'number') return 0;
			return hundredthsToTokens(this.sessionCooldownData.threshold);
		},
		sessionCooldownTimeBucketLabel () {
			const bucket = this.sessionCooldownData?.timeBucket;
			return this.getCooldownTimeBucketLabel(bucket) || bucket || '';
		},
		showSessionCooldownInfo () {
			return !!(
				this.isTeamSessionForCooldown &&
				this.hasTokenCooldownPermission &&
				this.sessionCooldownData &&
				typeof this.sessionCooldownData.tokensRemaining === 'number'
			);
		},
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			'purchaseSession',
			'bindSetupSessionPrefill',
			'updateTokenCooldownData',
			'updateSnackbar',
			'setMobiusEnabled',
		]),
		_isEmpty,
		openSessionCooldownMenu () {
			const menu = this.$refs.sessionCooldownMenu as { openMenu?: () => void };
			if (menu?.openMenu) menu.openMenu();
		},
		hydrateFromSetupSessionPrefill (
			appOffer: AppOffer,
			setupSessionPrefill: SetupSessionPrefill,
			hardwareOffers: HardwareOffer[],
			softwareOffers: SoftwareOffer[],
		) {
			if (this.shouldAttemptHydrateFromSessionPrefill) {
				this.hardwareOffer = null;
				this.softwareOffer = null;
				this.deploymentImage = null;

				if (!appOffer) return;

				if (this.mobiusEnabled) {
					const mobiusHardwareOffers = hardwareOffers.filter((h: HardwareOffer) => h.mobius);
					const appDeploymentImages = (this.deploymentImages || []).filter((d: DeploymentImage) => d.app === appOffer.app);

					// Pre-select the hardware + deployment image last used for this app (from the setup
					// prefill). `setupSessionPrefill.deploymentImage` is the Docker `repo:tag` (matches
					// DeploymentImage.repoRef). Falls back to the first mobius hardware and the
					// current-release image (or first available) when there's no usable prefill.
					const prefilledHardwareOffer: HardwareOffer = (
						setupSessionPrefill &&
						setupSessionPrefill.hardware &&
						_find(mobiusHardwareOffers, ({ hardware }) => hardware === setupSessionPrefill.hardware)
					) || null;
					const prefilledDeploymentImage: DeploymentImage = (
						setupSessionPrefill &&
						setupSessionPrefill.deploymentImage &&
						_find(appDeploymentImages, ({ repoRef }: DeploymentImage) => repoRef === setupSessionPrefill.deploymentImage)
					) || null;

					this.hardwareOffer = prefilledHardwareOffer || this._get(mobiusHardwareOffers, '[0]') || null;
					// Default to the same first item the picker shows at the top (shared sort),
					// so the visible top card and the pre-selection always agree.
					this.deploymentImage = prefilledDeploymentImage ||
						this._get(sortDeploymentImages(appDeploymentImages), '[0]') ||
						null;
					return;
				}

				const filteredHardwareOffers = hardwareOffers.filter(({ apps, mobius }) => !mobius && apps.includes(appOffer.app));
				const filteredSoftwareOffers = softwareOffers.filter(({ apps }) => apps.includes(appOffer.app));
				const firstHardwareOffer: HardwareOffer = this._get(filteredHardwareOffers, '[0]') || null;
				const firstSoftwareOffer: SoftwareOffer = _findLast(filteredSoftwareOffers, ({ releaseStatus }) => releaseStatus === RELEASE_STATUS.CURRENT) || this._get(filteredSoftwareOffers, '[0]') || null;

				let foundHardwareOffer: HardwareOffer = null;
				let foundSoftwareOffer: SoftwareOffer = null;
				if (setupSessionPrefill) {
					foundHardwareOffer = (setupSessionPrefill.hardware && _find(filteredHardwareOffers, ({ hardware }) => hardware === setupSessionPrefill.hardware)) || null;
					foundSoftwareOffer = (setupSessionPrefill.software && _find(filteredSoftwareOffers, ({ software }) => software === setupSessionPrefill.software)) || null;
				}

				this.hardwareOffer = foundHardwareOffer || firstHardwareOffer;
				this.softwareOffer = foundSoftwareOffer || firstSoftwareOffer;
			}
		},
		decideToForceWizardFlow (
			appOffer: AppOffer,
			boundPropsSetupSessionPrefill: { authUser: FirebaseUser, appKey: AppKey },
			loadingSetupSessionPrefill: boolean,
			setupSessionPrefill: SetupSessionPrefill,
		) {
			if (
				appOffer &&
				loadingSetupSessionPrefill === false && // loading state is not null and is done loading
				boundPropsSetupSessionPrefill &&
				appOffer.app === boundPropsSetupSessionPrefill.appKey &&
				!setupSessionPrefill
			) {
				// Doesn't have a setupSessionPrefill, so make them go through the wizard flow
				this.forceWizardFlow = true;
			}
		},
		async rolloverSetupSessionPrefillReleaseStatus (setupSessionPrefill: SetupSessionPrefill, softwareOffers: SoftwareOffer[]) {
			const softwareOffer: SoftwareOffer = (setupSessionPrefill && _find(softwareOffers, ['software', setupSessionPrefill.software])) || null;
			if (
				this.shouldAttemptHydrateFromSessionPrefill &&
				setupSessionPrefill
			) {
				const lockedReleaseStatus: ReleaseStatus = setupSessionPrefill.lockedReleaseStatus || RELEASE_STATUS.CURRENT;
				const lockedSoftwareOffer: SoftwareOffer = _find(softwareOffers, ['releaseStatus', lockedReleaseStatus]) || null;
				if (
					lockedSoftwareOffer &&
					(
						!softwareOffer ||
						lockedSoftwareOffer.bootVolume !== softwareOffer.bootVolume
					) &&
					(
						setupSessionPrefill.software !== lockedSoftwareOffer.software ||
						setupSessionPrefill.lockedReleaseStatus !== lockedSoftwareOffer.releaseStatus
					)
				) {
					try {
						const setupSessionPrefillRef = db
							.doc(`users/${this.user.id}/setupSessionPrefills/${setupSessionPrefill.id}`);
						await setupSessionPrefillRef.update({
							software: lockedSoftwareOffer.software,
							lockedReleaseStatus: lockedSoftwareOffer.releaseStatus,
						});
					} catch (e) {
						console.error(e);
					}
				}
			}
		},
		onWindowViewBack () {
			if (this.isNewUserTrialFlow) {
				if (this.windowView <= this.startingWindowViewForNewUserTrialFlow) {
					this.windowView = 0;
					this.goBackToSessionsPage(false);
				} else {
					this.windowView--;
				}
				return;
			}

			if (this.windowView > 0) {
				this.windowView--;
			} else {
				this.goBackToSessionsPage(false);
			}
		},
		onWindowViewForward () {
			if (this.windowView < this.LAUNCH_WINDOW_ITEM) {
				this.windowView++;
			}
		},
		goBackToSessionsPage (confirmCancelDialogOpenNewValue: boolean) {
			this.confirmCancelDialogOpen = !!confirmCancelDialogOpenNewValue;
			if (!this.confirmCancelDialogOpen) {
				if (this.$route.name === ROUTER.SETUP_ANOTHER_SESSION) {
					this.routerPush(this.$route, this.$router, { name: ROUTER.ANOTHER_SESSION, query: this.$route.query });
				} else {
					this.routerPush(this.$route, this.$router, { name: ROUTER.SESSIONS });
				}
			}
		},
		tryRoutingToOtherSingleOnlyAppSession () {
			if (this.appOffer) {
				let otherSingleOnlyAppSession: Session = null;
				this.currSessions.forEach((session: Session) => {
					if (session.apps.includes(this.appOffer.app)) {
						otherSingleOnlyAppSession = session;
					}
				});
				if (otherSingleOnlyAppSession) {
					const routeData = this.$router.resolve({
						name: ROUTER.LAUNCH,
						query: {
							sessionId: otherSingleOnlyAppSession.id,
						},
					});
					window.open(routeData.href, '_self');
					return;
				}
			}

			this.goBackToSessionsPage(false);
		},
		onPersistExtensionsInput (persistExtensions: boolean) {
			this.persistExtensions = !!persistExtensions;
			if (persistExtensions) setPersistExtensions();
			else removePersistExtensions();
		},
		onPickHardware () {
			this.windowView = this.PICK_HARDWARE_WINDOW_ITEM;
			this.dialogOpen = true;
		},
		onPickSoftware () {
			this.windowView = this.PICK_SOFTWARE_WINDOW_ITEM;
			this.dialogOpen = true;
		},
		onPickStorageOption () {
			this.windowView = this.PICK_STORAGE_OPTION_WINDOW_ITEM;
			this.dialogOpen = true;
		},
		onHardwareSelect (hardwareOffer: HardwareOffer) {
			this.shouldAttemptHydrateFromSessionPrefill = false;
			this.hardwareOffer = hardwareOffer || null;

			if (this.isWizardFlow) {
				this.onWindowViewForward();
			} else {
				this.dialogOpen = false;
			}
		},
		onSoftwareSelect ({ softwareOffer, keepView }) {
			this.shouldAttemptHydrateFromSessionPrefill = false;
			this.softwareOffer = softwareOffer || null;

			if (!keepView) {
				if (this.isWizardFlow) {
					this.onWindowViewForward();
				} else {
					this.dialogOpen = false;
				}
			}
		},
		onDeploymentImageSelect ({ deploymentImage, keepView }) {
			this.shouldAttemptHydrateFromSessionPrefill = false;
			this.deploymentImage = deploymentImage || null;

			if (!keepView) {
				if (this.isWizardFlow) {
					this.onWindowViewForward();
				} else {
					this.dialogOpen = false;
				}
			}
		},
		onWorkspaceSelect (workspace: Workspace) {
			this.selectedWorkspaceId = (workspace && workspace.id) || null;
			if (this.selectedWorkspaceId) {
				setWorkspaceId(this.selectedWorkspaceId);
			} else {
				removeWorkspaceId();
			}

			if (this.isWizardFlow) {
				this.onWindowViewForward();
			} else {
				this.dialogOpen = false;
			}
		},
		onSelectStorageOption (storageOptionValue: string) {
			if (this.isWizardFlow) {
				this.onWindowViewForward();
			} else {
				this.dialogOpen = false;
			}
		},
		getCooldownTimeBucketLabel (timeBucket: string | null | undefined): string {
			if (!timeBucket) return '';
			const map: Record<string, string> = {
				[TOKEN_COOLDOWN_TIME_BUCKET['1min']]: '1 minute',
				[TOKEN_COOLDOWN_TIME_BUCKET['2min']]: '2 minutes',
				[TOKEN_COOLDOWN_TIME_BUCKET['5min']]: '5 minutes',
				[TOKEN_COOLDOWN_TIME_BUCKET['10min']]: '10 minutes',
				[TOKEN_COOLDOWN_TIME_BUCKET['15min']]: '15 minutes',
				[TOKEN_COOLDOWN_TIME_BUCKET['30min']]: '30 minutes',
				[TOKEN_COOLDOWN_TIME_BUCKET['1hr']]: '1 hour',
				[TOKEN_COOLDOWN_TIME_BUCKET['2hr']]: '2 hours',
				[TOKEN_COOLDOWN_TIME_BUCKET['3hr']]: '3 hours',
				[TOKEN_COOLDOWN_TIME_BUCKET['12hr']]: '12 hours',
				[TOKEN_COOLDOWN_TIME_BUCKET['1day']]: '1 day',
				[TOKEN_COOLDOWN_TIME_BUCKET['3day']]: '3 days',
				[TOKEN_COOLDOWN_TIME_BUCKET['7day']]: '7 days',
				[TOKEN_COOLDOWN_TIME_BUCKET['30day']]: '30 days',
			};
			return map[timeBucket] || timeBucket;
		},
		async fetchTokenCooldownStatus () {
			if (!this.team?.id) return;
			try {
				const getTokenCooldownStatusFn = functions.httpsCallable('getTokenCooldownStatus');
				const { data } = await getTokenCooldownStatusFn({ teamId: this.team.id });
				if (!data?.success) return;
				if (data.resetAt != null) {
					this.updateTokenCooldownData({
						cooldownEndsAt: new Date(data.resetAt),
						cooldownData: {
							tokensRemaining: data.tokensRemaining,
							threshold: data.threshold,
							resetAt: data.resetAt,
							timeBucket: data.timeBucket,
							tokensSpent: data.tokensSpent,
						},
					});
				} else {
					// Cooldown does not apply (e.g. personal account or role has no cooldown) – clear state so button is not disabled
					this.updateTokenCooldownData({
						cooldownEndsAt: null,
						cooldownData: null,
					});
				}
			} catch (e) {
				console.error('fetchTokenCooldownStatus', e);
			}
		},
		async onLaunch () {
			if (this.sessionLaunchBlockedByDomainClaim) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: this.domainClaimLaunchMessage,
					show: true,
				});
				return;
			}
			if (this.launchDisabled) {
				return;
			}
			if (getProxyAdminToken() && !confirm(`You are proxying another user. Are you sure you want to use their account's balance to launch a session?`)) {
				return;
			}

			this.purchasing = true;
			const backendApps: AppKey[] = this.appOffer.backendApps || [];
			const options: SessionOptions = {
				// civitaiModels: [],
			};
			const { hardware } = this.hardwareOffer;
			// Mobius sessions are decoupled: no `apps`, no `software` — `deploymentImage` is the source of truth.
			const apps: AppKey[] | null = this.mobiusEnabled ? null : [this.appOffer.app];
			const software = this.mobiusEnabled ? null : this.softwareOffer.software;
			const deploymentImage: string | null = this.mobiusEnabled ? this.deploymentImage.repoRef : null;
			const region = this.mobiusEnabled
				? 'MOB-1'
				: ((this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM ? _get(this.team, 'region') : _get(this.user, 'region')) || null);

			const {
				success,
				sessionUuid,
			} = await this.purchaseSession({
				apps,
				backendApps,
				hardware,
				software,
				deploymentImage,
				options,
				apiEnabled: this.apiEnabled,
				persistExtensions: !!(
					this.shouldShowPersistExtensionsSetting &&
					this.persistExtensions
				),
				preloadModel: this.shouldSetCivitaiPreloadModel ? {
					provider: 'civitai',
					modelId: this.preloadModel.civitai.model.id,
					modelVersionId: this.preloadModel.civitai.modelVersion.id,
					downloadUrl: this.preloadModel.civitai.modelVersion.downloadUrl,
				} : null,
				purchaseMins: this.purchaseMins,
				displayName: `${this.currSessions.length + 1} - Session`,
				isTeamSession: !!(this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM && this.selectedWorkspaceId),
				workspaceId: this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM ? this.selectedWorkspaceId : null,
				region,
			});
			if (!success) {
				// Allow to try again
				this.purchasing = false;
			} else {
				this.routerReplace(this.$route, this.$router, {
					...this.$route,
					query: {
						...this.$route.query,
						...([ROUTER.ANOTHER_SESSION, ROUTER.SETUP_ANOTHER_SESSION].includes(this.$route.name) && {
							awaitSession: sessionUuid,
						}),
					},
				});
			}
		},
	},
	components: {
		UnlockRdTrial,
		PreloadModelPreview,
		EnablePersistExtensionsSettings,
		InfoMessageSmall,
		WindowStepper,
		ConfirmDialog,
		LoadingSVG,
		StorageOptionsList,
		BackButton,
		SetupSessionTerms,
		HardwareOffersList,
		SoftwareOffersList,
		DeploymentImagesList,
		FullSelectionPreviewCard,
		ActionsIsland,
		StorageCapCard,
		EnableApiSettings,
		HowToStopEarlyNotice,
		SessionSoundsSettings,
		TimeYourSessionInputs,
		BalanceCard,
		CooldownProgressMenu,
		CooldownLabelButton,
	},
});
